import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Card,
  CardContent,
  Typography,
  Container,
  Fade,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import React, { useState, useContext } from 'react';

// icons
import {
  ChevronRight as ChevronRightIcon,
  Assessment,
  Storage,
  Inventory,
  LocalShipping,
  History,
  ProcessedIcon,
  InventoryTwoTone,
} from '@mui/icons-material';
import DashBoardPageHeaderPage from '../components/DashBoard/DashBoardHeaderPage';
import ShipmentReport from '../components/ShipmentReport';
import ItemHistoryReport from '../components/ItemHistoryReport';
import StorageReport from '../components/StorageReport';
import FaultyReport from '../components/FaultyReport';
import ProcessedReport from '../components/ProcessedReport';
import TotalStockReport from './TotalStockReport';
import ThemeModeContext from '../context/ThemeModeContext';

const REPORTS = [
  {
    name: 'Inventory',
    icon: InventoryTwoTone,
    color: '#1976d2',
    description: 'Total stock overview',
  },
  {
    name: 'Storage',
    icon: Storage,
    color: '#2e7d32',
    description: 'Storage analytics',
  },
  {
    name: 'In-Process',
    icon: Assessment,
    color: '#ed6c02',
    description: 'Processing status',
  },
  {
    name: 'Processed',
    icon: Assessment,
    color: '#9c27b0',
    description: 'Completed items',
  },
  {
    name: 'Shipping Report',
    icon: LocalShipping,
    color: '#d32f2f',
    description: 'Shipment tracking',
  },
  {
    name: 'Item History',
    icon: History,
    color: '#0288d1',
    description: 'Item audit trail',
  },
];

export default function Reports() {
  const { isDarkMode } = useContext(ThemeModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedMenu, setSelectedMenu] = useState('Inventory');

  const handleSubmit = menu => {
    setSelectedMenu(menu);
  };

  const selectedReport = REPORTS.find(report => report.name === selectedMenu);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isDarkMode
          ? 'radial-gradient(ellipse at top, rgba(120, 119, 198, 0.1) 0%, transparent 70%)'
          : 'radial-gradient(ellipse at top, rgba(120, 119, 198, 0.05) 0%, transparent 70%)',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ py: { xs: 0.25, sm: 0.75 }, px: { xs: 1.5, sm: 2 } }}>
        <Fade in timeout={300}>
          <Box>
            {/* Enhanced Header */}
            <DashBoardPageHeaderPage title="Reports" />

            {/* Main Content */}
            <Grid container spacing={{ xs: 1, sm: 1.25 }} sx={{ mt: { xs: 0.5, sm: 0.75 } }}>
              {/* Sidebar Navigation */}
              <Grid item xs={12} md={4} lg={3}>
                <Card
                  elevation={0}
                  sx={{
                    border: `1px solid ${
                      isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                    }`,
                    borderRadius: { xs: 1.5, sm: 2 },
                    background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: isDarkMode
                      ? '0 2px 8px rgba(0,0,0,0.15)'
                      : '0 2px 8px rgba(0,0,0,0.06)',
                    position: 'sticky',
                    top: { xs: 12, sm: 16 },
                    maxHeight: { xs: 'calc(100vh - 100px)', sm: 'calc(100vh - 150px)' },
                    overflow: 'auto',
                  }}
                >
                  <CardContent sx={{ p: { xs: 1, sm: 1.25 } }}>
                    {/* Navigation Header */}
                    <Box sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          mb: 0.25,
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                          textTransform: 'uppercase',
                        }}
                      >
                        REPORT CATEGORIES
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' } }}
                      >
                        SELECT A REPORT TYPE TO VIEW ANALYTICS
                      </Typography>
                    </Box>

                    {/* Navigation Items */}
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 0.75 } }}
                    >
                      {REPORTS.map((report, i) => {
                        const IconComponent = report.icon;
                        const isSelected = selectedMenu === report.name;

                        return (
                          <Card
                            key={i}
                            elevation={0}
                            sx={{
                              cursor: 'pointer',
                              border: `1px solid ${
                                isSelected
                                  ? report.color
                                  : isDarkMode
                                  ? 'rgba(255,255,255,0.08)'
                                  : 'rgba(0,0,0,0.06)'
                              }`,
                              borderRadius: { xs: 1, sm: 1.5 },
                              background: isSelected
                                ? `linear-gradient(135deg, ${report.color}15 0%, ${report.color}08 100%)`
                                : 'transparent',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                transform: 'translateY(-0.5px)',
                                boxShadow: isDarkMode
                                  ? `0 2px 8px rgba(0,0,0,0.2)`
                                  : `0 2px 8px rgba(0,0,0,0.08)`,
                                border: `1px solid ${report.color}`,
                              },
                            }}
                            onClick={() => handleSubmit(report.name)}
                          >
                            <CardContent sx={{ p: { xs: 0.75, sm: 1 } }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: { xs: 1, sm: 1.25 },
                                }}
                              >
                                {/* Icon */}
                                <Box
                                  sx={{
                                    width: { xs: 24, sm: 28 },
                                    height: { xs: 24, sm: 28 },
                                    borderRadius: 1,
                                    background: isSelected
                                      ? `linear-gradient(135deg, ${report.color} 0%, ${report.color}cc 100%)`
                                      : isDarkMode
                                      ? 'rgba(255,255,255,0.08)'
                                      : 'rgba(0,0,0,0.04)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.15s ease',
                                  }}
                                >
                                  <IconComponent
                                    sx={{
                                      fontSize: { xs: 12, sm: 14 },
                                      color: isSelected ? 'white' : report.color,
                                    }}
                                  />
                                </Box>

                                {/* Content */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: isSelected ? report.color : 'text.primary',
                                      mb: 0.125,
                                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                      textTransform: 'uppercase',
                                      display: 'block',
                                    }}
                                  >
                                    {report.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: { xs: '0.55rem', sm: '0.6rem' },
                                      textTransform: 'uppercase',
                                      display: 'block',
                                    }}
                                  >
                                    {report.description}
                                  </Typography>
                                </Box>

                                {/* Arrow */}
                                <ChevronRightIcon
                                  sx={{
                                    fontSize: { xs: 12, sm: 14 },
                                    color: isSelected ? report.color : 'text.secondary',
                                    transform: isSelected ? 'translateX(1px)' : 'none',
                                    transition: 'all 0.15s ease',
                                  }}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Main Content Area */}
              <Grid item xs={12} md={8} lg={9}>
                <Fade in timeout={400} key={selectedMenu}>
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${
                        isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                      }`,
                      borderRadius: { xs: 1.5, sm: 2 },
                      background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.7)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: isDarkMode
                        ? '0 2px 8px rgba(0,0,0,0.15)'
                        : '0 2px 8px rgba(0,0,0,0.06)',
                      minHeight: { xs: 300, sm: 350 },
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      {/* Content Header */}
                      <Box
                        sx={{
                          p: { xs: 1.25, sm: 1.5 },
                          borderBottom: `1px solid ${
                            isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                          }`,
                          background: selectedReport
                            ? `linear-gradient(135deg, ${selectedReport.color}15 0%, ${selectedReport.color}08 100%)`
                            : 'transparent',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 1.25 },
                            mb: { xs: 1, sm: 1.25 },
                          }}
                        >
                          {selectedReport && (
                            <Box
                              sx={{
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                borderRadius: 1,
                                background: `linear-gradient(135deg, ${selectedReport.color} 0%, ${selectedReport.color}cc 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 2px 8px ${selectedReport.color}30`,
                              }}
                            >
                              <selectedReport.icon
                                sx={{ fontSize: { xs: 14, sm: 16 }, color: 'white' }}
                              />
                            </Box>
                          )}
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: selectedReport
                                  ? selectedReport.color
                                  : theme.palette.primary.main,
                                mb: 0.125,
                                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                textTransform: 'uppercase',
                                display: 'block',
                              }}
                            >
                              {selectedMenu} REPORT
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                textTransform: 'uppercase',
                                display: 'block',
                              }}
                            >
                              {selectedReport
                                ? selectedReport.description.toUpperCase()
                                : 'VIEW DETAILED ANALYTICS'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Active Status Chip */}
                        <Chip
                          label="ACTIVE REPORT"
                          size="small"
                          sx={{
                            bgcolor: selectedReport
                              ? `${selectedReport.color}20`
                              : `${theme.palette.primary.main}20`,
                            color: selectedReport
                              ? selectedReport.color
                              : theme.palette.primary.main,
                            fontWeight: 600,
                            fontSize: { xs: '0.55rem', sm: '0.6rem' },
                            height: { xs: 16, sm: 18 },
                            minWidth: { xs: '28px', sm: '32px' },
                          }}
                        />
                      </Box>

                      {/* Report Content */}
                      <Box sx={{ p: { xs: 1, sm: 1.25 } }}>
                        {selectedMenu === 'Inventory' && <TotalStockReport />}
                        {selectedMenu === 'Storage' && <StorageReport />}
                        {selectedMenu === 'In-Process' && <FaultyReport />}
                        {selectedMenu === 'Processed' && <ProcessedReport />}
                        {selectedMenu === 'Shipping Report' && <ShipmentReport />}
                        {selectedMenu === 'Item History' && <ItemHistoryReport />}
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
}
